import {useCallback, useEffect, useState} from 'react'
import {useAsync} from 'react-async-hook'

import {
  Box,
  Button,
  Feedback,
  Flex,
  Icon,
  IconButton,
  Link,
  ListItem,
  Modal,
  Option,
  Spinner,
  Text,
  UnorderedList,
  useToast,
} from '@ua/design-system'

import {editGroup, getGroups} from 'airship/clients/users'
import {BEES_GROUP, EARLY_ACCESS_GROUP} from 'airship/clients/users/constants'
import CustomerFeedbackModal from 'airship/components/CustomerFeedbackModal'
import getContext from 'airship/modules/page-context'
import type {PageContext} from 'airship/modules/report-error/initialize'

const FORM_ID = 'experimental-features-form'

type Props = {
  hasBeesToggle: boolean
}

const ExperimentalFeatureSettings: React.FC<Props> = ({hasBeesToggle}) => {
  const {showToast} = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const [beesState, setBeesState] = useState(false)
  const [pilotState, setPilotState] = useState(false)
  const [feedbackIsOpen, setFeedbackIsOpen] = useState(false)
  const context = getContext<PageContext>()

  const {
    loading: loadingGroups,
    error: groupsError,
    result: groups,
  } = useAsync(getGroups, [])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (groups !== undefined) {
      setBeesState(groups.includes(BEES_GROUP))
      setPilotState(groups.includes(EARLY_ACCESS_GROUP))
    }
  }, [groups])

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      setIsSaving(true)

      if (hasBeesToggle) {
        await editGroup({
          action: beesState ? 'add' : 'remove',
          group: BEES_GROUP,
        })
      }

      try {
        await editGroup({
          action: pilotState ? 'add' : 'remove',
          group: EARLY_ACCESS_GROUP,
        })
        window.location.reload()
      } catch {
        showToast({
          description: 'An error occurred, please try again.',
          variant: 'error',
        })
        setIsSaving(false)
      }
    },
    [beesState, hasBeesToggle, pilotState, showToast]
  )

  return (
    <>
      <IconButton
        icon={<Icon name="flask" weight="regular" size="sm" />}
        aria-label="Airship Pilot"
        tooltip
        variant="hollow"
        size="sm"
        bg="background.secondary"
        onClick={() => setIsOpen(true)}
      />
      <Modal
        size="lg"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        heading="Airship Pilot"
        footer={
          <Flex direction="row" gap="4xs">
            <Button onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              type="submit"
              form={FORM_ID}
              isLoading={isSaving}
            >
              Save and reload
            </Button>
          </Flex>
        }
      >
        <form id={FORM_ID} onSubmit={onSubmit}>
          <Text mb="3xs">
            Get early access to enhancements and help shape the product
            direction through your feedback.
          </Text>
          {loadingGroups && <Spinner />}
          {groupsError && (
            <Feedback variant="error">
              An error occurred, please try again later.
            </Feedback>
          )}
          <Box borderRadius="md" borderWidth="xs" mb="3xs">
            {hasBeesToggle && (
              <Box borderBottomWidth="none">
                <Option
                  label="Enable bees features 🐝"
                  onChange={(checked) => setBeesState(checked)}
                  isChecked={beesState}
                  isDisabled={isSaving}
                />
              </Box>
            )}
            <Box
              borderTopLeftRadius={'inherit'}
              borderTopRightRadius={'inherit'}
            >
              <Option
                label="Enable all pilot enhancements"
                onChange={setPilotState}
                isChecked={pilotState}
                isDisabled={isSaving}
              />
            </Box>
            <Flex
              direction="column"
              gap="4xs"
              p="xs"
              borderWidth="xs"
              borderBottomWidth="none"
            >
              <Text fontWeight="bold">June 2025 Release</Text>
              <Text>
                The June 2025 UX - Improved Navigation - introduces a
                streamlined navigation experience with a refreshed left-side
                nav, searchable navigation, and a new Favorites section for
                quick access to your most-used tools.
              </Text>
              <UnorderedList>
                <ListItem>
                  <Text as="span" fontWeight="bold">
                    Create new:
                  </Text>{' '}
                  Simplified Create menu for faster access to Journeys, Scenes
                  and more
                </ListItem>
                <ListItem>
                  <Text as="span" fontWeight="bold">
                    Left side nav:
                  </Text>{' '}
                  Updated left nav for faster access to key tools and views.
                </ListItem>
                <ListItem>
                  <Text as="span" fontWeight="bold">
                    Navigation search:
                  </Text>{' '}
                  Quickly find pages and tools using the new navigation search
                  bar.
                </ListItem>
                <ListItem>
                  <Text as="span" fontWeight="bold">
                    Favorites:
                  </Text>{' '}
                  Save frequently used nav items for easy access in the new
                  Favorites section.
                </ListItem>
                <ListItem>
                  <Text as="span" fontWeight="bold">
                    Project links:
                  </Text>{' '}
                  Access useful project links like Settings and Brand Guidelines
                  through the top-left menu.
                </ListItem>
              </UnorderedList>
            </Flex>
            <Box
              p="xs"
              borderTopWidth="xs"
              borderBottomLeftRadius="inherit"
              borderBottomRightRadius="inherit"
            >
              <Button
                size="sm"
                onClick={() => {
                  setIsOpen(false)
                  setFeedbackIsOpen(true)
                }}
              >
                Submit feedback
              </Button>
            </Box>
          </Box>
          <Feedback variant="info">
            <Flex direction="column" gap="5xs">
              <Text variant="bodySmall" fontWeight="bold">
                Please note:
              </Text>
              <UnorderedList>
                <ListItem>
                  <Text variant="bodySmall">
                    Features may be modified or removed entirely.
                  </Text>
                </ListItem>
                <ListItem>
                  <Text variant="bodySmall">
                    Features may contain bugs or reliability issues.
                  </Text>
                </ListItem>
              </UnorderedList>
              <Text variant="bodySmall">
                For more information, please review our Beta Services Terms at{' '}
                <Link href="https://www.airship.com/legal/beta-terms/">
                  https://www.airship.com/legal/beta-terms/
                </Link>
              </Text>
            </Flex>
          </Feedback>
        </form>
      </Modal>
      <CustomerFeedbackModal
        isOpen={feedbackIsOpen}
        onClose={() => setFeedbackIsOpen(false)}
        name={context.context_processors.sentry_data.USER?.username}
        email={context.context_processors.sentry_data.USER?.email}
      />
    </>
  )
}

export default ExperimentalFeatureSettings
